import { Controller } from '@hotwired/stimulus';
import Collapse from 'bootstrap/js/src/collapse';

const CLASS_HIDDEN = 'd-none';
const SELECTOR_PANEL = '.amba-resident-panel';
// Keyed on the option rather than on the row's category, because the option id is what
// the manifest names a row by: a row missing that hook has to be absent from the lookup
// rather than present under a key nothing asks for.
const SELECTOR_ROW = 'tbody tr[data-alert-notification-option]';

// The page's view, as the address bar spells it. The floor keeps the name the
// server-side select posts under, so one parameter serves both halves of the filter bar
// and the controller reading it back has one reader rather than two.
const PARAM_FLOOR = 'summaries[view_group]';
const PARAM_SEARCH = 'search';
const PARAM_CATEGORIES = 'categories[]';
const PARAM_URGENCIES = 'urgencies[]';
// D-48: each toggle is in the URL only while it differs from its default, spelled as the
// VIP dashboard spells include_checks.
const PARAM_INCLUDE_CHECKS = 'include_checks';
const PARAM_ALERTS_ONLY = 'alerts_only';
// Written by the server after a save, never by this controller: it names the setting
// that was saved, which is the one the page should come back to.
const PARAM_SETTING = 'vip_property_id';
// Long enough that a burst of typing settles once, short enough that a click landing
// straight after the last keystroke is the only case that has to flush by hand.
const SETTLE_DELAY = 250;
// This controller's own half of the view. The floor is not here: the server owns it,
// and the select that sets it carries it by itself.
const FILTER_PARAMS = [
  PARAM_SEARCH,
  PARAM_CATEGORIES,
  PARAM_URGENCIES,
  PARAM_INCLUDE_CHECKS,
  PARAM_ALERTS_ONLY,
];
// The row's edit link leaves the page entirely, so it takes the floor along too.
const CARRIED_PARAMS = [PARAM_FLOOR, ...FILTER_PARAMS];
// Marks the hidden inputs this controller puts into the floor form, so a later submit
// rewrites its own fields instead of stacking a second set on top of them.
const CARRIED_FIELD = 'data-alert-tracking-filters-carried';

/**
 * Search, category-pill and urgency-pill filtering across every resident table on the
 * Alert Tracking page. The two pill dimensions compose: a row survives only if it
 * satisfies every dimension that has a pressed pill.
 *
 * Both work over the whole filtered set rather than the panel the user happens to have
 * open — and rather than the rows the server happened to render, which is what the
 * manifest is for (see #buildIndex). What a filter does is narrow the list: a panel's open
 * state is the operator's own, and no filter changes it in either direction. A matching
 * panel is left shut, exactly as a plain page load leaves it, and the operator opens the
 * resident they came for.
 *
 * Since Phase 33 a panel's rows are not there to hide until the panel has been expanded
 * and its body fetched. That changes nothing about what matches: the manifest is the set,
 * so a resident is found and counted whether or not their rows exist yet. Two things
 * follow from it, both deliberate.
 *
 * A filter fetches nothing. It opens no panel, and a shut panel is `display: none`, so the
 * `loading="lazy"` frame inside it has nothing to intersect. A one-letter search matching
 * three hundred residents costs three hundred class toggles and not one request.
 *
 * A body that arrives while a filter is pressed arrives filtered (#frameWillRender). The
 * filter is applied to the fetched rows before Turbo puts them in the page, so an expanded
 * panel never flashes the rows the operator has just filtered away.
 *
 * Since Phase 34 the list is one batch of the set and grows as it is scrolled, which the
 * manifest already covered: a resident matches and is counted whether or not their panel
 * has been reached. A batch that arrives is linked to the index and filtered on arrival
 * (#panelsAppended), and a filter change restarts the list rather than filtering what has
 * already been appended to it (#restartList).
 *
 * Floor filtering is deliberately not here: it is access control and belongs to the
 * server-side form above this controller's element.
 *
 * Both dimensions are mirrored into the address bar as they change, because a save
 * leaves the page and comes back through a full load: the URL is the only thing that
 * survives it. It is also what the row's edit link hands to the settings form, and what
 * the save's redirect echoes back.
 */
export default class extends Controller {
  static targets = [
    'search',
    'pill',
    'checkToggle',
    'alertsOnlyToggle',
    'status',
    'clear',
    'manifest',
    'moreToLoad',
    'alertCount',
  ];
  // The redirect's own ceiling on the search text, handed over from the single Ruby
  // constant that sets it (AlertTrackingViewState::MAX_SEARCH_LENGTH) so that the two
  // ways this state travels cannot drift apart.
  static values = { maxSearchLength: Number };

  connect() {
    this.index = this.#buildIndex();
    this.#linkRendered();
    // The list's own starting state, so a filter change can put it back (see #restartList).
    this.moreToLoadMarkup = this.hasMoreToLoadTarget
      ? this.moreToLoadTarget.innerHTML
      : '';
    // A body arrives already filtered, or it arrives wrong. Turbo's frame events bubble,
    // so one delegated listener covers every panel.
    this.element.addEventListener(
      'turbo:before-frame-render',
      this.#frameWillRender
    );
    // A batch of panels appended by the scroll controller. Same contract as a fetched
    // body: what arrives arrives filtered.
    this.element.addEventListener(
      'infinite-scroll.loaded',
      this.#panelsAppended
    );
    // The address bar describes the view, so it — not whatever a reload happened to hand
    // the search box back — is what the first pass makes the rows agree with.
    this.#restoreFromUrl();
    this.apply();
    this.#revealRequestedSetting();
  }

  disconnect() {
    clearTimeout(this.settleTimer);
    this.element.removeEventListener(
      'turbo:before-frame-render',
      this.#frameWillRender
    );
    this.element.removeEventListener(
      'infinite-scroll.loaded',
      this.#panelsAppended
    );
  }

  search() {
    this.#applyAndSync();
  }

  togglePill(event) {
    const pill = event.currentTarget;
    if (pill.dataset.allPill !== undefined) {
      this.#exclusivePills().forEach((p) =>
        p.setAttribute('aria-pressed', 'false')
      );
    } else {
      pill.setAttribute(
        'aria-pressed',
        String(pill.getAttribute('aria-pressed') !== 'true')
      );
    }
    this.#syncAllPill();
    this.#applyAndSync();
  }

  toggleCheck() {
    this.#applyAndSync();
  }

  toggleAlertsOnly() {
    this.#applyAndSync();
  }

  clear() {
    this.searchTarget.value = '';
    this.pillTargets.forEach((pill) =>
      pill.setAttribute('aria-pressed', 'false')
    );
    if (this.hasCheckToggleTarget) this.checkToggleTarget.checked = true;
    if (this.hasAlertsOnlyToggleTarget)
      this.alertsOnlyToggleTarget.checked = false;
    this.#syncAllPill();
    this.#applyAndSync();
    this.searchTarget.focus();
  }

  // The row's edit link is server-rendered with a fixed href, so the view goes onto it
  // at click time. The address bar is the one source both this and a reload read — and
  // `delete` before `append` keeps a second click from stacking the same values up
  // again.
  carryViewState({ currentTarget }) {
    // The write is debounced, so the URL may still be a keystroke behind the rows. This
    // is the one reader that cannot afford to wait.
    this.#flushSync();

    const here = new URLSearchParams(window.location.search);
    const outgoing = new URL(currentTarget.href, window.location.origin);

    CARRIED_PARAMS.forEach((name) => {
      outgoing.searchParams.delete(name);
      here
        .getAll(name)
        .forEach((value) => outgoing.searchParams.append(name, value));
    });

    currentTarget.href = outgoing.toString();
  }

  // The floor select posts a GET form, and a GET form is serialised from its own fields
  // — so submitting it rewrites the whole query string and drops anything not in it.
  // Same job as carryViewState, different carrier: hidden inputs where a link has a
  // query string.
  //
  // Two things deliberately stay out. The floor, because the select itself is the field
  // that carries it. And the setting name a save arrives with, because changing floor is
  // not returning from a save and nothing should scroll — it is absent from
  // FILTER_PARAMS, so it cannot ride along even before `connect` has spent it.
  carryViewStateOnSubmit({ currentTarget }) {
    // Same race as the link: the URL write is debounced, and a floor changed straight
    // after a keystroke would otherwise carry the search the page loaded with.
    this.#flushSync();

    const here = new URLSearchParams(window.location.search);

    currentTarget
      .querySelectorAll(`[${CARRIED_FIELD}]`)
      .forEach((field) => field.remove());

    FILTER_PARAMS.forEach((name) => {
      here.getAll(name).forEach((value) => {
        const field = document.createElement('input');

        field.type = 'hidden';
        field.name = name;
        field.value = value;
        field.setAttribute(CARRIED_FIELD, '');
        currentTarget.appendChild(field);
      });
    });
  }

  // `announce: false` for a pass the operator did not ask for. The match count comes off
  // the manifest, so an arriving batch cannot change it — saying the same number again into
  // a live region for every batch the list grows by is noise, not information.
  apply({ announce = true } = {}) {
    // No bar means no categories to offer and nothing to search.
    if (!this.hasSearchTarget) return;

    const criteria = this.#criteria();
    let matchedPanels = 0;

    // D-49: the stylesheet hides every quiet-rows footer under this, including one in a
    // body fetched later.
    this.element.dataset.alertsOnly = String(criteria.alertsOnly);

    this.index.forEach((entry) => {
      const visible = this.#filterRows(entry, criteria);

      entry.panel?.classList.toggle(CLASS_HIDDEN, !visible);
      if (visible) matchedPanels += 1;
    });

    if (announce) this.#announce(matchedPanels, criteria.filtering);
  }

  // A batch of panels, filtered on arrival.
  //
  // The panels are already in the page by the time this runs — the scroll controller
  // appends them and then fires — so unlike a fetched body they cannot be filtered before
  // they are painted. What can be kept is that they are never left unfiltered: the index
  // has described them since connect (the manifest is the whole set), so all this has to
  // do is link them to it and re-run the pass.
  #panelsAppended = () => {
    this.#linkRendered();
    this.apply({ announce: false });
  };

  // A filter change restarts the list rather than filtering what has already been appended
  // to it: the set being shown changed, so showing its start is the truthful thing, and it
  // hands back the node count the batching exists to bound. The batches then arrive again
  // as the operator scrolls, filtered on arrival, so restarting cannot lose a match that
  // sits further down the list.
  //
  // Only the appended panels go. The first batch's are the ones the page was rendered with
  // — removing and refetching those would throw away every body already fetched into them.
  #restartList() {
    if (!this.hasMoreToLoadTarget) return;

    this.element.querySelectorAll(SELECTOR_PANEL).forEach((panel) => {
      if (!this.firstBatch.has(panel)) panel.remove();
    });

    this.moreToLoadTarget.innerHTML = this.moreToLoadMarkup;
    this.#linkRendered();
    // The link is back, but the observer watching it is not: it saw this element enter the
    // viewport once already and will not report it again. Dispatched from inside the scroll
    // controller's element so it bubbles to it.
    this.dispatch('restart', { target: this.moreToLoadTarget });
  }

  // The filter bar, read once per pass rather than per entry.
  #criteria() {
    const query = this.#normalize(this.searchTarget.value);
    const categories = this.#pressedValues('category');
    const urgencies = this.#pressedValues('urgency');
    const includeChecks = this.hasCheckToggleTarget
      ? this.checkToggleTarget.checked
      : true;
    const alertsOnly = this.hasAlertsOnlyToggleTarget
      ? this.alertsOnlyToggleTarget.checked
      : false;

    return {
      query,
      categories,
      urgencies,
      includeChecks,
      alertsOnly,
      filtering:
        Boolean(query) ||
        categories.size > 0 ||
        urgencies.size > 0 ||
        !includeChecks ||
        alertsOnly,
    };
  }

  // Whether this resident matches, and — for the rows that are on screen — which of them
  // do. The two are deliberately separate: the count comes off the manifest, so a resident
  // whose body has not loaded matches, and is counted, exactly as if it had. What a body
  // being absent costs is only the hiding, and that is settled the moment it arrives
  // (#frameWillRender).
  #filterRows(
    entry,
    { query, categories, urgencies, includeChecks, alertsOnly }
  ) {
    const panelMatches = !query || entry.haystack.includes(query);
    let visibleRows = 0;
    const visibleCounts = { critical: 0, flag: 0 };

    entry.rows.forEach((row) => {
      const inCategory = categories.size === 0 || categories.has(row.category);
      const inUrgency = urgencies.size === 0 || urgencies.has(row.urgency);
      const matchesQuery = panelMatches || row.haystack.includes(query);
      const passesCheck = includeChecks || !row.check;
      const hasAlerts = !alertsOnly || row.alertCount > 0;
      const visible =
        inCategory && inUrgency && matchesQuery && passesCheck && hasAlerts;

      row.element?.classList.toggle(CLASS_HIDDEN, !visible);
      if (visible) {
        visibleRows += 1;
        visibleCounts.critical += row.criticalCount;
        visibleCounts.flag += row.flagCount;
      }
    });

    this.#updateAlertCountPill(entry, visibleCounts);

    return visibleRows > 0;
  }

  // D-47: the header's three fixed nodes, written from the manifest's per-level counts on
  // every pass rather than compared with what an earlier pass wrote, so a collapsed or
  // appended panel reads the same as an opened one.
  #updateAlertCountPill(entry, { critical, flag }) {
    const counts = entry.panel?.querySelector(
      '[data-alert-tracking-filters-target="alertCount"]'
    );
    if (!counts) return;

    [
      ['high', critical],
      ['low', flag],
    ].forEach(([level, count]) => {
      const pill = counts.querySelector(`[data-count-pill="${level}"]`);
      if (!pill) return;

      pill.querySelector('[data-count]').textContent =
        pill.dataset.countFormat.replace('%{count}', count);
      pill.classList.toggle(CLASS_HIDDEN, count === 0);
    });

    counts
      .querySelector('[data-quiet]')
      ?.classList.toggle(CLASS_HIDDEN, critical + flag > 0);
  }

  // A deferred body, filtered before it is on screen.
  //
  // `turbo:before-frame-render` and not `turbo:frame-load`, because the difference is
  // visible: the fetched rows are hidden while they are still in the response document,
  // so a row the filter excludes is never painted. Turbo moves those nodes into the page
  // rather than re-parsing them (FrameRenderer.renderElement extracts a Range), so the
  // classes set here are the classes that land.
  //
  // Nothing else in `apply` is repeated. The panel is already visible — the operator
  // expanding it is what fetched this — and the match count was never waiting on the body,
  // so re-announcing would say the same number twice into a live region for every panel
  // opened.
  #frameWillRender = (event) => {
    // The same guard `apply` opens with, and for the same reason: `#criteria` reads the
    // search box. Unreachable today — a panel implies an option, and an option implies a
    // filter bar — but if that ever stops holding, an unguarded throw in here is a body
    // landing unfiltered rather than a visible failure.
    if (!this.hasSearchTarget) return;

    const entry = this.#entryForFrame(event.target);
    if (!entry) return;

    this.#adoptRows(entry, event.detail.newFrame);
    this.#filterRows(entry, this.#criteria());
  };

  // The frame is inside its own resident's panel, which is how it is keyed back to the
  // manifest entry — the frame's own id is a server-side naming convention this side has
  // no business parsing.
  #entryForFrame(frame) {
    const panel = frame.closest?.(SELECTOR_PANEL);
    if (!panel) return null;

    return this.entriesByVip.get(panel.dataset.residentPanel) ?? null;
  }

  // Links a fetched body's rows back onto the manifest entries that have been standing in
  // for them since connect. Keyed on the option id, the same key #buildIndex uses, so a row
  // the manifest does not describe is left alone rather than guessed at.
  #adoptRows(entry, container) {
    const arrived = new Map(
      Array.from(container.querySelectorAll(SELECTOR_ROW)).map((element) => [
        element.dataset.alertNotificationOption,
        element,
      ])
    );

    entry.rows.forEach((row) => {
      const element = arrived.get(row.id);
      if (!element) return;

      row.element = element;
      row.vipProperty = element.dataset.vipProperty;
    });
  }

  // Built once on connect, out of the manifest the server ships with the page rather than
  // out of the rendered rows. The rendered rows are about to stop being the set: a panel's
  // body loads when it is expanded, and panels arrive as the list is scrolled. An index
  // read off the DOM would then be blind to everyone not on screen — a search that
  // silently misses a resident, which on this page is a clinician not finding a setting.
  // The manifest covers the whole filtered set, so what is rendered no longer decides
  // what is findable.
  //
  // Each entry links to its rendered panel and rows where they exist, and matches all the
  // same where they do not: an entry with nothing on screen still counts towards the match
  // count, and has nothing to hide or reveal.
  //
  // Haystacks are composed here from the manifest's own fields, in the same two joins the
  // page's two hooks used to supply — header name plus detail line, row category plus
  // setting label — and normalised through the one #normalize the term goes through.
  #buildIndex() {
    // Keyed as well as listed: `apply` walks the whole index, but a frame arriving names
    // one resident and has to reach their entry without a scan per body.
    this.entriesByVip = new Map();

    return this.#manifest().map((resident) => {
      const entry = {
        id: String(resident.id),
        // Filled in by #linkRendered, for the residents that are on screen.
        panel: null,
        haystack: this.#normalize([resident.name, resident.detail].join(' ')),
        rows: resident.rows.map((row) => ({
          // Kept, because a row's element arrives later than its entry does: this is
          // what a fetched body is matched back onto (#adoptRows).
          id: String(row.id),
          element: null,
          category: row.category,
          urgency: row.urgency,
          check: Boolean(row.check),
          alertCount: row.alert_count || 0,
          criticalCount: row.critical_count || 0,
          flagCount: row.flag_count || 0,
          haystack: this.#normalize([row.category, row.label].join(' ')),
        })),
      };

      this.entriesByVip.set(entry.id, entry);

      return entry;
    });
  }

  // Points the index at whatever the page is currently showing. Run at connect, and again
  // whenever the list changes shape under it — a batch appended, or a filter change
  // restarting it.
  //
  // Assigned rather than merged, so it heals in both directions: a panel that has gone
  // loses its reference instead of leaving the index toggling classes on a detached node.
  // Nothing else survives a panel — the filter keeps no per-panel state — so dropping the
  // reference is the whole unlink. The manifest describes residents the page has not
  // reached, and those simply stay unlinked: they match, they count, and there is nothing
  // to show or hide.
  #linkRendered() {
    const rendered = this.#renderedByKey();

    // The batch the server rendered the page with, taken on the first pass: it is what
    // tells an appended panel from an original one when the list is restarted.
    this.firstBatch ??= new Set(rendered.panels.values());

    this.index.forEach((entry) => {
      entry.panel = rendered.panels.get(entry.id) ?? null;

      entry.rows.forEach((row) => {
        row.element = rendered.rows.get(row.id) ?? null;
        // Off the element, not the manifest: the only reader of it scrolls to the row,
        // which it can only do for one that is on screen.
        row.vipProperty = row.element?.dataset.vipProperty;
      });
    });
  }

  #manifest() {
    if (!this.hasManifestTarget) return [];

    return JSON.parse(this.manifestTarget.textContent).residents;
  }

  // Two traversals and then constant-time lookups, rather than a selector query per entry:
  // an attribute query is a document scan, and one per row is quadratic in the size of the
  // organisation. Phase 34 re-runs it when new panels arrive (see #linkRendered), which is
  // what the shape was chosen for: two traversals per batch, not one per panel.
  #renderedByKey() {
    const keyed = (selector, attribute) =>
      new Map(
        Array.from(this.element.querySelectorAll(selector)).map((element) => [
          element.dataset[attribute],
          element,
        ])
      );

    return {
      panels: keyed(SELECTOR_PANEL, 'residentPanel'),
      rows: keyed(SELECTOR_ROW, 'alertNotificationOption'),
    };
  }

  // The rows keep up with every keystroke. The two things that cost something wait for the
  // operator to stop typing.
  //
  // The URL, because Safari throws SecurityError past roughly a hundred history writes in
  // thirty seconds, and a throw here would escape the action after `apply()` had already
  // moved the rows, leaving the address bar describing a view the page is no longer
  // showing.
  //
  // The restart, because it refetches. `apply()` above has already filtered the appended
  // batches in place, so all deferring the teardown costs is nodes, and only until the
  // typing stops — where restarting per keystroke would walk the list again for every
  // letter of a term whose matches are sparse.
  #applyAndSync() {
    this.apply();
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.#settle(), SETTLE_DELAY);
  }

  #settle() {
    this.#syncUrl();
    this.#restartList();
  }

  // The URL alone, for the one reader that cannot wait for it. Deliberately not the
  // restart: a link being clicked is not the moment to tear the list down and fetch it
  // again, so the pending one is dropped with the timer and leaves nothing behind but
  // nodes.
  #flushSync() {
    clearTimeout(this.settleTimer);
    this.#syncUrl();
  }

  #syncUrl() {
    if (!this.hasSearchTarget) return;

    const url = new URL(window.location);
    const query = this.#boundedQuery();

    if (query) url.searchParams.set(PARAM_SEARCH, query);
    else url.searchParams.delete(PARAM_SEARCH);

    this.#writeList(url, PARAM_CATEGORIES, this.#pressedValues('category'));
    this.#writeList(url, PARAM_URGENCIES, this.#pressedValues('urgency'));

    const { includeChecks, alertsOnly } = this.#criteria();
    this.#writeList(url, PARAM_INCLUDE_CHECKS, includeChecks ? [] : ['0']);
    this.#writeList(url, PARAM_ALERTS_ONLY, alertsOnly ? ['1'] : []);

    // Only this controller's own parameters are touched, so the floor the server put
    // there survives untouched.
    this.#replaceUrl(url);
  }

  // Bounded here, at the one point the search leaves this controller for the address
  // bar, so everything downstream of the URL — the row's link and the floor form alike —
  // is bounded by having read it from there. Guarded rather than defaulted: a missing
  // value must not read as a ceiling of zero.
  #boundedQuery() {
    const query = this.searchTarget.value.trim();

    return this.maxSearchLengthValue > 0
      ? query.slice(0, this.maxSearchLengthValue)
      : query;
  }

  // `replaceState`, not push: a filter is a change of view, not a step the back button
  // should have to walk back one keystroke at a time.
  //
  // The existing state object is carried through rather than replaced with null. Turbo
  // keeps its restoration bookkeeping there and its popstate handler returns early
  // without it, so writing null would leave Back changing the address bar and nothing
  // else, and would desync Turbo's sense of which way it is travelling from then on.
  #replaceUrl(url) {
    history.replaceState(history.state, '', url);
  }

  #writeList(url, name, values) {
    url.searchParams.delete(name);
    values.forEach((value) => url.searchParams.append(name, value));
  }

  #restoreFromUrl() {
    if (!this.hasSearchTarget) return;

    const params = new URLSearchParams(window.location.search);
    const categories = new Set(params.getAll(PARAM_CATEGORIES));
    const urgencies = new Set(params.getAll(PARAM_URGENCIES));

    this.searchTarget.value = params.get(PARAM_SEARCH) ?? '';
    this.pillTargets.forEach((pill) => {
      const { category, urgency } = pill.dataset;
      const pressed =
        category === undefined
          ? urgencies.has(urgency)
          : categories.has(category);

      pill.setAttribute('aria-pressed', String(pressed));
    });
    this.#syncAllPill();

    if (this.hasCheckToggleTarget)
      this.checkToggleTarget.checked = params.get(PARAM_INCLUDE_CHECKS) !== '0';
    if (this.hasAlertsOnlyToggleTarget)
      this.alertsOnlyToggleTarget.checked =
        params.get(PARAM_ALERTS_ONLY) === '1';
  }

  // A save comes home naming the setting it changed. Every way of not getting there is
  // ordinary: the row may be filtered out by the very filters just restored, on a floor
  // the page is no longer showing, or gone because the save is what stopped it alerting.
  // None of those may throw, and none may leave the page scrolled somewhere arbitrary,
  // so a target that is not both present and visible is simply not scrolled to.
  //
  // Runs after the filter pass, so the row it settles on is one the filters have already
  // left standing.
  #revealRequestedSetting() {
    const url = new URL(window.location);
    const requested = url.searchParams.get(PARAM_SETTING);
    if (!requested) return;

    // Spent on arrival, found or not, and spent before the row is looked for rather than
    // after. The rest of the URL describes the view and stays, but "the row a save just
    // came back to" stops being true the moment the operator scrolls away — so leaving it
    // would have every later reload and every Turbo restoration visit re-centre on a
    // stale row, overriding both where the operator had scrolled to and the position
    // Turbo itself had just restored.
    //
    // Spending it first is what makes that a guarantee rather than a likelihood: the
    // lookup below cannot leave the parameter behind by failing, whatever it is one day
    // changed to do.
    url.searchParams.delete(PARAM_SETTING);
    this.#replaceUrl(url);

    // `row.element` is the whole guard, and `entry.panel` deliberately does not get the
    // `?.` its sibling in `apply` has. A keyed row only ever exists inside its own rendered
    // panel — a deferred body renders into the frame inside that panel, and a panel
    // appended later brings its rows with it — so a row on screen means a panel on
    // screen. Guarding it would be worse than leaving it:
    // `!entry.panel?.classList.contains(...)` reads a *missing* panel as a visible one, and
    // #expandThenScroll would then be handed null. If that implication is ever broken, this
    // should throw and say so rather than quietly scroll the page somewhere arbitrary.
    //
    // Phase 33 is what makes the lookup find anything at all at this point. It runs once,
    // in `connect`, and every panel's body is deferred — so the only rows on screen are the
    // ones the server rendered inline, which is exactly the one panel a save came home to
    // (Organisations::AlertTrackingController#eager_panel_options). Waiting for a frame
    // instead would mean forcing a fetch on a panel that is still `display: none`, then
    // racing Turbo's own scroll-to-top; shipping the one body the scroll needs is the
    // cheaper and the more certain of the two.
    const target = this.index
      .flatMap((entry) => entry.rows.map((row) => ({ entry, row })))
      .find(
        ({ entry, row }) =>
          row.element &&
          row.vipProperty === requested &&
          !row.element.classList.contains(CLASS_HIDDEN) &&
          !entry.panel.classList.contains(CLASS_HIDDEN)
      );
    if (!target) return;

    this.#expandThenScroll(target.entry.panel, target.row.element);
  }

  // Panels default collapsed, and a row inside a collapsed panel has no height to scroll
  // to — so the scroll waits for the panel to finish opening. This is the one place that
  // opens a panel the operator did not click: it is their own destination, arrived at
  // through a save, and no filter opens anything.
  #expandThenScroll(panel, row) {
    const collapse = panel.querySelector('.collapse');

    if (!collapse || collapse.classList.contains('show')) {
      this.#scrollTo(row);
      return;
    }

    collapse.addEventListener('shown.bs.collapse', () => this.#scrollTo(row), {
      once: true,
    });
    Collapse.getOrCreateInstance(collapse, { toggle: false }).show();
  }

  // Focus deliberately stays where the page load left it: the save's own confirmation is
  // the flash at the top, and pulling focus into the table would read straight past it.
  #scrollTo(element) {
    // A frame later, because Turbo scrolls a freshly rendered page to the top and a
    // scroll issued before that is undone by it.
    requestAnimationFrame(() => element.scrollIntoView({ block: 'center' }));
  }

  #exclusivePills() {
    return this.pillTargets.filter((p) => p.dataset.allPill === undefined);
  }

  #syncAllPill() {
    const all = this.pillTargets.find((p) => p.dataset.allPill !== undefined);
    if (!all) return;
    const anyPressed = this.#exclusivePills().some(
      (p) => p.getAttribute('aria-pressed') === 'true'
    );
    all.setAttribute('aria-pressed', String(!anyPressed));
  }

  // One pill target list, one dimension per data attribute: a pill carries either a
  // category or an urgency, never both, so the dimensions read independently off the
  // same targets and stay composable.
  #pressedValues(dimension) {
    return new Set(
      this.pillTargets
        .filter((pill) => pill.dataset[dimension] !== undefined)
        .filter((pill) => pill.getAttribute('aria-pressed') === 'true')
        .map((pill) => pill.dataset[dimension])
    );
  }

  #announce(count, filtering) {
    const { matchCountOne, matchCountOther, noMatches } =
      this.statusTarget.dataset;
    let message = '';

    if (filtering) {
      if (count === 0) {
        message = noMatches;
      } else if (count === 1) {
        message = matchCountOne;
      } else {
        message = matchCountOther.replace('%{count}', count);
      }
    }

    // The region stays in the accessibility tree and only its text changes: a live
    // region revealed at the same moment it gains content is not reliably announced.
    this.statusTarget.textContent = message;
    this.clearTarget.classList.toggle(CLASS_HIDDEN, !filtering);
  }

  // Same normalisation as the organisation search, so the two in-page searches treat
  // apostrophes, hyphens and spacing alike.
  #normalize(text) {
    return (
      text?.toLowerCase().replace(/['-]/g, '').replace(/\s+/g, ' ').trim() ?? ''
    );
  }
}
